/**
 * Reservations that own a window but no hour yet, given one.
 *
 * The recertification assessment is the case. `applyRecertRule` decides *which
 * days* it may happen on — days 56 to 60 of the certification period — and which
 * clinician it belongs to, but it runs while the plan is being expanded, before
 * the tenant's shifts and visits have been fetched, so it cannot know where the
 * free time is. This module is the second half: once the horizon lands, every
 * reservation with a window and no position is placed into free time inside that
 * window, on its own clinician, by the same search the SOC hold uses.
 *
 * It runs from the reducer on `SERVER_SNAPSHOT` and `TENANT_BOUND`, beside
 * `linkPlanVisits`. It is idempotent by construction: a reservation that already
 * carries an hour is left exactly where it is, so the search cannot move
 * something under a clinician's hand, and a reload cannot shuffle the week.
 *
 * No fit inside the window is not an error and does not move the reservation out
 * of its window. It stays without an hour, which the week draws as a card that
 * needs a time and the worklist carries as a row.
 *
 * **No hierarchy between reservations** (Pete, 2026-09-21). A real visit displaces
 * a reservation; reservations never displace each other, so the first one placed
 * keeps its span and every later search goes around it. The pass below is
 * therefore only ever triggered by a fact — a visit a clinician has committed
 * onto the span, or a block — and never by another promise.
 */
import type { DateKey, DemoState, Hold, PlanVisit } from "@caredna-marlin/utils/homeHealthDemo/state";
import { addDaysToKey, dateKeyAt, isWorkingDayKey, weekStartKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { providerIdFor } from "./clinicianDay";
import { openSpanOnDay, type OpenSpanQuery } from "./referralChecks";
import { hasTraits, isInRamp, isSocCapable, rosterEntries } from "./roster";
import { planVisitLayers } from "./utilization";
import { recertTargetDate, recertWindow } from "@caredna-marlin/utils/homeHealthDemo/engine/rules";
import { weekIndexFor } from "@caredna-marlin/utils/homeHealthDemo/engine/planExpansion";

/**
 * The window a plan visit may move inside.
 *
 * The recertification owns a real window — days 56 to 60 of the certification
 * period — and may be placed anywhere in it. **Every other reservation that
 * already claims an hour owns exactly its own day**, and is here so that the
 * collision check below can see it: a pending-auth placeholder took its hour
 * from `PROVISIONAL_VISIT_START` in the fixtures and kept it whatever else the
 * clinician had committed to that span, which is how one came to be drawn over
 * a real visit on Dana's Monday (Pete, 2026-09-21). A reservation with no hour
 * and no window stays without one; nothing here invents a time.
 */
export function windowDaysFor(state: DemoState, visit: PlanVisit): DateKey[] {
  if (visit.rule !== "recert") {
    // **A beyond-auth placeholder owns a week, not an hour** (script author,
    // discussion turn 46). It is a slot because the plan gave it a day, and its
    // compliance window is the frequency week that day sits in — so the window
    // survives losing the hour, which is what lets the search run again and lets
    // the worklist say *needs a time* with the window named. Deriving it beats a
    // flag: a flag would be a new state on the visit, which is the thing turn 42
    // is an argument against.
    if (planVisitLayers(state)[visit.id] === "provisional") {
      const weekStart = weekStartKey(visit.targetDate);
      return Array.from({ length: 7 }, (_, i) => addDaysToKey(weekStart, i)).filter(isWorkingDayKey);
    }
    return visit.windowStart !== undefined ? [visit.targetDate] : [];
  }
  const episode = state.episodes[visit.episodeId];
  if (!episode) return [];
  const { start, end } = recertWindow(episode);
  const days: DateKey[] = [];
  for (let day = start; day <= end; day = addDaysToKey(day, 1)) if (isWorkingDayKey(day)) days.push(day);
  return days;
}

/** `"HH:mm"` plus a run of minutes, as `"HH:mm"`. */
export function windowEndFor(windowStart: string, minutes: number): string {
  const [h, m] = windowStart.split(":").map(Number);
  const end = h * 60 + m + minutes;
  return `${String(Math.floor(end / 60)).padStart(2, "0")}:${String(end % 60).padStart(2, "0")}`;
}

/**
 * Minutes a reservation for this visit keeps: the visit plus the drive allowance,
 * the same size a start-of-care hold reserves. It is what it is promising to
 * hold, not what the visit itself occupies.
 */
export function reservationMinutes(state: Pick<DemoState, "config">, visit: PlanVisit): number {
  return state.config.visitTypes[visit.visitTypeKind].plannedMinutes + state.config.socSlot.driveMinutes;
}

/**
 * Place every reservation that has a window, a clinician and no hour. Returns the
 * same object when nothing moved, so the reducer can keep the previous reference.
 */
export function placeReservations(state: DemoState): Record<string, PlanVisit> {
  // **Seeded with the whole map, then mutated in place.** The searches below read
  // `working` so that a reservation placed earlier in this pass is an obstacle to
  // the next one. It has to start as the *complete* set: built up entry by entry
  // it was a half-populated plan, so `planVisitLayers` re-derived authorization
  // coverage from a fragment of each episode on every call, which is both wrong
  // and a fresh object per call for the layer cache to miss on.
  const out: Record<string, PlanVisit> = { ...state.planVisits };
  const working: DemoState = { ...state, planVisits: out };
  let changed = false;
  const tz = state.config.branch.timeZone;
  const providerIdOf = new Map(rosterEntries(state).map((e) => [e.handle, providerIdFor(state, e)]));
  // Every promise on the board, so the displacement check below can be told to
  // ignore all of them at once: only a fact may move a reservation off its span.
  const everyReservation = { excludeHoldIds: Object.values(state.holds).map((h) => h.id), excludePlanVisitIds: Object.keys(state.planVisits) };
  for (const [id, visit] of Object.entries(state.planVisits)) {
    out[id] = visit;
    if (visit.status !== "placeholder" || visit.providerHandle === undefined) continue;
    const providerId = providerIdOf.get(visit.providerHandle);
    if (providerId === undefined) continue;
    const days = windowDaysFor(state, visit);
    if (!days.length) continue;
    const needMinutes = reservationMinutes(state, visit);

    // It is where it is, and a fact — a visit somebody committed onto the span,
    // or a block — is now standing on it. Only a fact can do this: no
    // reservation is ever placed on top of another.
    //
    // **An hour with no end is still an hour.** The pending-auth placeholder is
    // staged with `windowStart: PROVISIONAL_VISIT_START` and no `windowEnd`, so
    // a guard that required both skipped it entirely and it kept 13:00 whatever
    // the day had committed to that span — which is how one came to be drawn
    // beside a real visit on Dana's Monday after the first fix was supposed to
    // have moved it (Pete, 2026-09-21: *"I'm still seeing it after refreshing
    // the page and resetting the store"*). A reservation with a start and no end
    // is promising `reservationMinutes` from that start, so that is what the
    // collision is measured against.
    let placed = visit;
    const claimedEnd = placed.windowEnd ?? (placed.windowStart === undefined ? undefined : windowEndFor(placed.windowStart, needMinutes));
    if (placed.windowStart !== undefined && claimedEnd !== undefined && collides(state, { ...placed, windowEnd: claimedEnd }, providerId, tz, everyReservation)) {
      placed = { ...placed, windowStart: undefined, windowEnd: undefined };
    }
    if (placed.windowStart !== undefined) continue;

    let found = false;
    for (const day of days) {
      // **Against what this pass has already placed**, not against the state it
      // started from. Two reservations searched in one pass used to be invisible
      // to each other and could be dealt the same span — which Pete's rule of
      // 2026-09-22 forbids and a whole-cast spec caught.
      const fit = openSpanOnDay(working, providerId, day, { needMinutes, excludePlanVisitIds: [id] });
      if (!fit.time) continue;
      out[id] = { ...placed, targetDate: day, weekIndex: weekIndexFor(state.episodes[placed.episodeId] ?? { socDate: day }, day), windowStart: fit.time, windowEnd: windowEndFor(fit.time, needMinutes) };
      found = true;
      break;
    }
    // Nowhere inside the window. It goes back to the day the rule would have
    // targeted anyway, with no hour: the week reads it as needing a time, and
    // its points sit where they always sat.
    if (!found) {
      const episode = state.episodes[placed.episodeId];
      const fallback = placed.rule === "recert" && episode ? recertTargetDate(episode) : placed.targetDate;
      // Same day, same absence of an hour: hand back the object we were given,
      // so a state nothing happened to keeps its identity and the selectors
      // downstream keep their memoization.
      if (fallback !== visit.targetDate || visit.windowStart !== undefined) {
        out[id] = { ...placed, targetDate: fallback, weekIndex: episode ? weekIndexFor(episode, fallback) : placed.weekIndex };
      }
    }
    if (out[id] !== visit) changed = true;
  }
  return changed ? out : state.planVisits;
}

/**
 * The same thing for a **hold**, which is a reservation that is not a plan visit.
 *
 * A seeded hold takes its hour from the cast fixture — `hold.windowStart`, or
 * 08:00 when the fixture gives none — and kept it whatever else the clinician
 * had committed to that span. On Dana's Monday that drew *Reserved · held for
 * SOC* across three real visits, with the fixture's `afternoon` caption under it
 * as the tell (Pete, 2026-09-21; the script author read it the same way). The
 * model says a reservation is placed into free time and never on top of
 * anything, so a hold standing on a fact is searched again on its own day.
 *
 * Its day does not move: the start of care is due inside a window somebody else
 * decided. **If nothing on that day fits, the hold gives up its hour and its
 * clinician** and becomes the *Needs a time* row the worklist already knows how
 * to draw — rule 3 of the model, and the last place the old defect lived (script
 * author, discussion turns 42–46). It used to keep the hour it had, which is how
 * one came to be painted across three of Dana’s visits.
 *
 * A hold in that state is **retried every time placement runs**, on its own day,
 * across the clinicians who could take a start of care that day. So a day that
 * frees up later reclaims it, and the row is an exception somebody resolves
 * rather than a state the hold lives in.
 */
export function placeHolds(state: DemoState): Record<string, Hold> {
  const out: Record<string, Hold> = { ...state.holds };
  const working: DemoState = { ...state, holds: out };
  let changed = false;
  const tz = state.config.branch.timeZone;
  const providerIdOf = new Map(rosterEntries(state).map((e) => [e.handle, providerIdFor(state, e)]));
  const socTraits = state.config.visitTypes.SOC.requiredTraits;
  /** Who could take a start of care on this day: the same filter the accept-path search uses, minus its clock. */
  const takersOn = (day: DateKey) =>
    rosterEntries(state).filter((e) => e.roleKind === "assessing" && !isInRamp(e, day) && isSocCapable(e, day) && hasTraits(e, socTraits));

  for (const [id, hold] of Object.entries(state.holds)) {
    out[id] = hold;
    if (hold.status !== "held") continue;
    const needMinutes = Math.round((dateKeyAt(hold.date, hold.windowEnd, tz).getTime() - dateKeyAt(hold.date, hold.windowStart, tz).getTime()) / 60_000);
    if (needMinutes <= 0) continue;

    /**
     * Needs a time, looking for one: retried on its own day, every pass — and on
     * **its own clinician's** day when intake named one. A hold authored on
     * Marcus that loses its hour is still Marcus's; letting the search walk the
     * roster put it on Dana, in a morning the seed keeps for the start of care
     * that 1.3 re-holds there (2026-09-24). A hold nobody assigned is genuinely
     * free to go to whoever has room, which is what this loop was written for.
     */
    if (hold.providerHandle === undefined) {
      const candidates = hold.authoredHandle ? takersOn(hold.date).filter((e) => e.handle === hold.authoredHandle) : takersOn(hold.date);
      for (const entry of candidates) {
        const pid = providerIdOf.get(entry.handle);
        if (pid === undefined) continue;
        const fit = openSpanOnDay(working, pid, hold.date, { needMinutes, excludeHoldIds: [id] });
        if (!fit.time) continue;
        out[id] = { ...hold, providerHandle: entry.handle, windowStart: fit.time, windowEnd: windowEndFor(fit.time, needMinutes) };
        changed = true;
        break;
      }
      continue;
    }

    const providerId = providerIdOf.get(hold.providerHandle);
    if (providerId === undefined) continue;
    // **Itself only.** A hold yields to anything already standing on its span,
    // a reservation included — which does not break rule 4, because the hold
    // moves itself rather than displacing anybody. A reservation's own search
    // goes around every hold that was placed before it, so anything found here
    // was placed when this hold was not, and this hold is the later one. Holds
    // used to exempt every reservation from this check, and a seeded hold on its
    // fixture hour therefore sat on a placed reservation for ever.
    const here = openSpanOnDay(working, providerId, hold.date, { excludeHoldIds: [id], notBefore: hold.windowStart, needMinutes });
    if (here.time === hold.windowStart) continue; // where it is, is free
    const fit = openSpanOnDay(working, providerId, hold.date, { needMinutes, excludeHoldIds: [id] });
    if (fit.time && fit.time !== hold.windowStart) {
      out[id] = { ...hold, windowStart: fit.time, windowEnd: windowEndFor(fit.time, needMinutes) };
      changed = true;
      continue;
    }
    // Nothing on the day holds it. It is not a hold at that hour; it is a row.
    out[id] = { ...hold, providerHandle: undefined };
    changed = true;
  }
  return changed ? out : state.holds;
}

/**
 * A fact is standing where this reservation is: a real visit or a block. Another
 * reservation cannot be, because the search that placed it treated this one as
 * busy — nothing here yields to a promise.
 */
function collides(state: DemoState, visit: PlanVisit, providerId: number, tz: string, everyReservation: Pick<OpenSpanQuery, "excludeHoldIds" | "excludePlanVisitIds">): boolean {
  const start = dateKeyAt(visit.targetDate, visit.windowStart!, tz).getTime();
  const end = dateKeyAt(visit.targetDate, visit.windowEnd!, tz).getTime();
  const fit = openSpanOnDay(state, providerId, visit.targetDate, {
    ...everyReservation,
    notBefore: visit.windowStart,
    needMinutes: Math.round((end - start) / 60_000),
  });
  return fit.time !== visit.windowStart;
}
